// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import Clickable from "@insite/mobius/Clickable";
import Link, { StyledIcon, StyledTypography } from "@insite/mobius/Link";
import ChevronLeft from "@rugby/XDL/Icons/ChevronLeft";
import ChevronRight from "@rugby/XDL/Icons/ChevronRight";
import Close from "@rugby/XDL/Icons/Close";

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ButtonIcon } from "@insite/mobius/Button";
import LazyImage from "@insite/mobius/LazyImage";
import VisuallyHidden from "@insite/mobius/VisuallyHidden/VisuallyHidden";
import { XngMenusContext } from "@rugby/App/Components/XngMenu/XngMenu.Provider";
import {
    mainNavigationImageSideStyles,
    StyledLinkWrapper,
    StyledMenu,
    StyledMenuHeader,
    StyledMenuHeaderAddress,
    StyledMenuHeaderText,
    StyledMenuHeaderTitle,
    StyledMenuItem,
    StyledTooltip,
} from "@rugby/App/Components/XngMenu/XngMenu.Styles";
import {
    canUseDOM,
    createUniversalPortal,
    isMobile,
    xngCalculateTooltipPosition,
} from "@rugby/App/Components/XngMenu/XngMenu.utils";
import { xHeaderState } from "@rugby/App/Store/Reducers";
import { isEventSupported, xAddEventListener, xRemoveAllEventListeners } from "@rugby/XDL/Utilities/utilities";
import throttle from "lodash/throttle";
import React, { useContext, useEffect, useState } from "react";
import { connect } from "react-redux";

const XngMenuItems = ({ menu, isSubMenu, fullWidth, withOverlay, menuId, children }: any) => {
    const { showMenu, hideMenu, activeMenus } = useContext(XngMenusContext);
    let items;
    const isMobileActive = isMobile();
    if (isSubMenu && !(activeMenus as any)[menuId]) {
        items = null;
    } else {
        items = menu.children.map((item: any, index: number) => {
            const subMenuId = `${item.title}_${index}_${menuId}`;
            if (item.children?.length > 0) {
                return (
                    <StyledMenuItem
                        key={item.url}
                        onMouseOver={
                            !isMobileActive
                                ? ev => {
                                      ev.stopPropagation();
                                      showMenu(subMenuId, {
                                          menuId: subMenuId,
                                          title: item.title,
                                          parentId: menuId,
                                          image: item.image,
                                          url: item.url,
                                          withOverlay,
                                      });
                                  }
                                : undefined
                        }
                        onClick={
                            isMobileActive
                                ? ev => {
                                      ev.stopPropagation();
                                      showMenu(subMenuId, {
                                          menuId: subMenuId,
                                          title: item.title,
                                          parentId: menuId,
                                          image: item.image,
                                          url: item.url,
                                          withOverlay,
                                      });
                                  }
                                : undefined
                        }
                        onMouseLeave={
                            !isMobileActive
                                ? ev => {
                                      ev.stopPropagation();
                                      hideMenu(subMenuId);
                                  }
                                : undefined
                        }
                    >
                        <Link href={item.url}>{item.title}</Link>
                        <StyledIcon
                            className={`link-chevron ${(activeMenus as any)[subMenuId] ? "chevron-visible" : ""}`}
                            size={9}
                            src={ChevronRight}
                        />
                        <XngMenuItems
                            isSubMenu
                            fullWidth={fullWidth}
                            menu={item}
                            menuId={subMenuId}
                            withOverlay={withOverlay}
                        />
                    </StyledMenuItem>
                );
            }

            const itemId = `menu_${item.url}`;
            return (
                item && (
                    <StyledMenuItem
                        key={item.url}
                        onMouseOver={
                            !isMobileActive
                                ? ev => {
                                      ev.stopPropagation();
                                      showMenu(itemId, {
                                          menuId: itemId,
                                          title: item.title,
                                          parentId: "menuId",
                                          image: item.image,
                                          url: item.url,
                                          withOverlay,
                                      });
                                  }
                                : undefined
                        }
                        onMouseLeave={
                            !isMobileActive
                                ? ev => {
                                      ev.stopPropagation();
                                      hideMenu(itemId);
                                  }
                                : undefined
                        }
                    >
                        <Link href={item.url}>{item.title}</Link>
                    </StyledMenuItem>
                )
            );
        });
    }

    return (
        <StyledMenu
            className={
                isSubMenu ? ((activeMenus as any)[menuId] ? "sub-menu menu-opened" : "sub-menu") : "main-menu-item"
            }
            fullWidth={fullWidth}
        >
            <StyledWrapper className="overflow-wrapper">
                <StyledWrapper className="scroll-wrapper">
                    <StyledWrapper>
                        {items}
                        {children}
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </StyledMenu>
    );
};

const XngMenu = ({
    menuTrigger,
    children,
    menuItem,
    fullWidth,
    menuId,
    withOverlay,
    triggers,
    isHeaderMenu,
    appendTo,
    isSticky,
    type,
    ...otherProps
}: any) => {
    const { showMenu, hideMenu, activeMenus, activeMenu, hideAllMenus } = useContext(XngMenusContext);
    const refElement: any = React.useRef<HTMLElement>();
    const arrowRef: any = React.useRef<HTMLElement>();
    const tooltipRef: any = React.useRef<HTMLElement>();

    const { image, url, title, address } = menuItem;
    const boundariesElement = isHeaderMenu && canUseDOM() ? document.querySelector("header header div") : undefined;
    const [isScrollListenerEnable, setIsScrollListenerEnable] = useState(false);

    // RUG-942 - To solve SSR warning issue
    const [isMounted, setIsMounted] = useState(false);

    useEffect(() => {
        // RUG-942 - To solve SSR warning issue
        setIsMounted(true);
    }, []);

    const virtualScroll = function (element: any, event: any) {
        let d = 0;
        if (event.wheelDelta) {
            d = event.wheelDelta || -event.detail;
        } else if (event.deltaY !== 0) {
            // for Firefox
            d = -event.deltaY;
        }

        // eslint-disable-next-line react/no-this-in-sfc
        element.scrollTop += (d < 0 ? 1 : -1) * 30;
    };

    useEffect(() => {
        if (!isMobile()) {
            handleMenuScroll();
        }
    }, [activeMenus]);

    const handleMenuScroll = () => {
        // To fix firefox issue, it doesn't support mousewheel event
        const mouseWheelEvent = isEventSupported("mousewheel") ? "mousewheel" : "wheel";
        const numOfMenuItem = Object.keys(activeMenus).length || 0;
        if (numOfMenuItem === 0) {
            setIsScrollListenerEnable(false);
        } else if (numOfMenuItem === 1 && !isScrollListenerEnable) {
            let _eventHandlers = {};

            // Stop Main menu (xng-menu) to scroll
            Array.from(document.getElementsByClassName("xng-menu")).forEach(element => {
                element.addEventListener(mouseWheelEvent, function (e) {
                    e.preventDefault();
                });
                element.addEventListener("DOMMouseScroll", function (e) {
                    e.preventDefault();
                });
            });

            // for Mega menu - Main Items
            const mainItemsElement = document
                ?.getElementsByClassName("main-menu-item")[0]
                ?.getElementsByClassName("scroll-wrapper")[0];
            if (mainItemsElement) {
                const mainItemsElementHandler = (e: Event) => {
                    if (document.querySelectorAll(".sub-menu:hover").length) {
                        const subMenu = mainItemsElement
                            ?.getElementsByClassName("menu-opened")[0]
                            ?.getElementsByClassName("scroll-wrapper")[0];

                        if (!subMenu) {
                            return;
                        }

                        subMenu.addEventListener(mouseWheelEvent, function (subMenuEvent) {
                            virtualScroll(subMenuEvent.currentTarget, subMenuEvent);
                        });

                        subMenu.addEventListener("DOMMouseScroll", function (subMenuEvent) {
                            virtualScroll(subMenuEvent.currentTarget, subMenuEvent);
                        });

                        const newEventHandlers = xAddEventListener(
                            subMenu,
                            [mouseWheelEvent, "DOMMouseScroll"],
                            function (subMenuEvent: any) {
                                virtualScroll(subMenuEvent.currentTarget, subMenuEvent);
                            },
                            false,
                            _eventHandlers,
                        );
                        _eventHandlers = { ..._eventHandlers, ...newEventHandlers };
                        return;
                    }

                    xRemoveAllEventListeners(undefined, [mouseWheelEvent, "DOMMouseScroll"], _eventHandlers);
                    _eventHandlers = {};

                    virtualScroll(e.currentTarget, e);
                };
                // virtualScroll
                mainItemsElement.addEventListener(mouseWheelEvent, mainItemsElementHandler);
                mainItemsElement.addEventListener("DOMMouseScroll", mainItemsElementHandler);
            }
            setIsScrollListenerEnable(true);
        }
    };

    const reCalculateTooltipPosition: any = React.useCallback(
        throttle((event: any) => {
            if (!tooltipRef.current) {
                return;
            }
            repositionToolTip();
        }, 400),
        [tooltipRef.current, activeMenus],
    );

    const mouseEnterHandler = React.useCallback((event: MouseEvent) => {
        showMenu(menuId, { menuId, withOverlay, image, url, title, mainMenu: true, parentId: "mainMenu", address });
    }, []);

    const mouseLeaveHandler = React.useCallback((event: MouseEvent) => {
        if (tooltipRef.current?.querySelector("input:focus, select:focus")) {
            return;
        }
        hideMenu(menuId);
    }, []);

    const mouseClickHandler = React.useCallback(
        (event: MouseEvent) => {
            event.stopImmediatePropagation();
            if (!(activeMenus as any)[menuId]) {
                mouseEnterHandler(event);
            } else {
                mouseLeaveHandler(event);
            }
        },
        [activeMenus],
    );

    useEffect(() => {
        if (tooltipRef.current) {
            document.addEventListener("scroll", reCalculateTooltipPosition, { passive: true });
            window.addEventListener("resize", reCalculateTooltipPosition);

            if ((!triggers || (triggers.indexOf("click") === -1 && triggers.indexOf("focus") === -1)) && !isMobile()) {
                tooltipRef.current?.addEventListener("mouseover", mouseEnterHandler);
                tooltipRef.current?.addEventListener("mouseleave", mouseLeaveHandler);
                refElement.current?.addEventListener("mouseover", mouseEnterHandler);
                refElement.current?.addEventListener("mouseleave", mouseLeaveHandler);
            } else if (isMobile() || triggers.indexOf("click") > -1) {
                refElement.current?.addEventListener("click", mouseClickHandler);
            }

            return () => {
                document.removeEventListener("scroll", reCalculateTooltipPosition);
                window.removeEventListener("resize", reCalculateTooltipPosition);

                if (
                    (!triggers || (triggers.indexOf("click") === -1 && triggers.indexOf("focus") === -1)) &&
                    !isMobile()
                ) {
                    tooltipRef.current?.removeEventListener("mouseover", mouseEnterHandler);
                    tooltipRef.current?.removeEventListener("mouseleave", mouseLeaveHandler);
                    refElement.current?.removeEventListener("mouseover", mouseEnterHandler);
                    refElement.current?.removeEventListener("mouseleave", mouseLeaveHandler);
                } else if (isMobile() || triggers.indexOf("click") > -1) {
                    refElement.current?.removeEventListener("click", mouseClickHandler);
                }
            };
        }
    }, [tooltipRef.current, activeMenus]);

    const repositionToolTip = () => {
        if (tooltipRef.current) {
            const styles = xngCalculateTooltipPosition(tooltipRef.current, refElement.current, boundariesElement);
            tooltipRef.current.style.opacity = `${styles.opacity}`;
            tooltipRef.current.style.maxWidth = `${styles.maxWidth}`;
            tooltipRef.current.style.transform = `translate3d(${styles.left},${styles.top},0)`;
            arrowRef.current && (arrowRef.current.style.transform = `translate3d(${styles.arrowLeft},0,0)`);
        }
    };

    useEffect(() => {
        repositionToolTip();
        setTimeout(() => {
            repositionToolTip();
        }, 400);
    }, [isSticky, activeMenus]);

    let menu: any;
    let menuHeader: any;

    if (isHeaderMenu && (activeMenu as any)) {
        const parentId = (activeMenu as any).parentId;
        menuHeader = (
            <StyledMenuHeader>
                {(activeMenu as any).menuId !== parentId && (
                    <Clickable
                        onClick={(ev: any) => {
                            ev.stopPropagation();
                            hideMenu((activeMenu as any).menuId);
                            showMenu(parentId, (activeMenus as any)[parentId]);
                        }}
                        aria-labelledby="back-menu"
                    >
                        <ButtonIcon src={ChevronLeft} size={16} />
                        <VisuallyHidden id="back-menu">back</VisuallyHidden>
                    </Clickable>
                )}
                <StyledTypography {...StyledMenuHeaderText}>
                    <StyledTypography {...StyledMenuHeaderTitle}>{(activeMenu as any).title}</StyledTypography>
                    <StyledTypography {...StyledMenuHeaderAddress}>{(activeMenu as any).address}</StyledTypography>
                </StyledTypography>
                <Clickable
                    onClick={(ev: any) => {
                        ev.stopPropagation();
                        hideAllMenus();
                    }}
                    aria-labelledby="close-menu"
                >
                    <ButtonIcon src={Close} size={16} />
                    <VisuallyHidden id="close-menu">Close Menu</VisuallyHidden>
                </Clickable>
            </StyledMenuHeader>
        );
    }

    if (children && type === "tooltip") {
        menu = (
            <StyledTooltip
                className={`xng-menu${otherProps.className ? ` ${otherProps.className}` : ""}`}
                ref={tooltipRef}
                isVisible={(activeMenus as any)[menuId]}
                isHeaderMenu={isHeaderMenu}
                arrow
            >
                {(activeMenus as any)[menuId] && <span ref={arrowRef} className="arrow"></span>}
                {(activeMenus as any)[menuId] && menuHeader}
                {(activeMenus as any)[menuId] && children}
            </StyledTooltip>
        );
    } else {
        menu = (
            <StyledTooltip
                ref={tooltipRef}
                fullWidth={fullWidth}
                isVisible={(activeMenus as any)[menuId]}
                isHeaderMenu={isHeaderMenu}
                className={`xng-menu${otherProps.className ? ` ${otherProps.className}` : ""}`}
            >
                <StyledWrapper style={{ position: "relative" }}>
                    {(activeMenus as any)[menuId] && menuHeader}
                    {(activeMenus as any)[menuId] && (
                        <XngMenuItems menuId={menuId} menu={menuItem} fullWidth={fullWidth} withOverlay={withOverlay}>
                            {children}
                        </XngMenuItems>
                    )}
                </StyledWrapper>
                {(activeMenus as any)[menuId] && activeMenu && (activeMenu as any).image && (
                    <StyledWrapper className="menu-image" {...mainNavigationImageSideStyles.container}>
                        <Clickable href={(activeMenu as any).url} {...mainNavigationImageSideStyles.clickableContainer}>
                            <LazyImage src={(activeMenu as any).image} {...mainNavigationImageSideStyles.image} />
                        </Clickable>
                    </StyledWrapper>
                )}
            </StyledTooltip>
        );
    }

    // RUG-942 - To solve SSR warning issue
    if (!isMounted) {
        return <></>;
    }

    return (
        <>
            {createUniversalPortal(menu, appendTo)}
            <StyledLinkWrapper ref={refElement} className={(activeMenus as any)[menuId] ? "menu-opened" : null}>
                {menuTrigger}
            </StyledLinkWrapper>
        </>
    );
};

const mapStateToProps = (state: xHeaderState) => ({
    location: getLocation(state),
    isSticky: state.xHeader.isSticky,
});

XngMenu.defaultProps = {
    trigger: "hover",
    type: "tooltip",
};

export default connect(mapStateToProps)(XngMenu);
